// Envío de la cola sin cobertura: primero las fotos guardadas en el dispositivo, después las revisiones (con detección de conflictos).
import { api } from "./gmao";
import * as offline from "./offline";

function replaceTempFiles(obj, map) {
  if (Array.isArray(obj)) return obj.map((x) => replaceTempFiles(x, map));
  if (obj && typeof obj === "object") return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, replaceTempFiles(v, map)]));
  return typeof obj === "string" && map[obj] ? map[obj] : obj;
}

function collectTempFiles(obj, out = new Set()) {
  if (Array.isArray(obj)) obj.forEach((x) => collectTempFiles(x, out));
  else if (obj && typeof obj === "object") Object.values(obj).forEach((v) => collectTempFiles(v, out));
  else if (offline.isTempFile(obj)) out.add(obj);
  return out;
}

async function uploadTempFiles(op) {
  const temps = [...collectTempFiles(op.payload)];
  const map = { ...(op.file_map || {}) };
  for (const t of temps) {
    if (map[t]) continue;
    const f = await offline.fileGet(t);
    if (!f?.blob) continue;
    const fd = new FormData(); fd.append("file", f.blob, f.name || "foto.jpg");
    const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    map[t] = data.id;
  }
  if (Object.keys(map).length) await offline.queueUpdate({ ...op, file_map: map });
  return map;
}

// Devuelve {ok, conflicts, errors}. Los conflictos quedan en la cola con status "conflict" hasta que el usuario decide.
export async function syncQueue({ onlyIds, force = false } = {}) {
  if (offline.isOffline()) return { ok: 0, conflicts: [], errors: [], offline: true };
  let ops = await offline.queueList();
  ops = onlyIds ? ops.filter((o) => onlyIds.includes(o.op_id)) : ops.filter((o) => o.status !== "conflict" && o.status !== "error");
  if (!ops.length) return { ok: 0, conflicts: [], errors: [] };
  const prepared = [];
  for (const op of ops) {
    const map = await uploadTempFiles(op);
    prepared.push({ op_id: op.op_id, type: op.type, session_id: op.session_id, element_id: op.element_id, client_at: op.client_at, force: force || !!op.force, payload: replaceTempFiles(op.payload, map) });
  }
  const { data } = await api.post("/sync/push", { ops: prepared });
  const out = { ok: 0, conflicts: [], errors: [] };
  for (const r of data.results || []) {
    const op = ops.find((o) => o.op_id === r.op_id);
    if (r.status === "ok") {
      out.ok += 1;
      await offline.queueRemove(r.op_id);
      for (const t of collectTempFiles(op?.payload || {})) await offline.fileRemove(t).catch(() => {});
    } else if (r.status === "conflict") {
      out.conflicts.push({ ...r, op });
      await offline.queueUpdate({ ...op, status: "conflict", conflict: r });
    } else {
      out.errors.push({ ...r, op });
      await offline.queueUpdate({ ...op, status: "error", error: r.message });
    }
  }
  offline.notifyOfflineChange();
  return out;
}

export async function discardOp(opId) {
  const ops = await offline.queueList();
  const op = ops.find((o) => o.op_id === opId);
  if (op) for (const t of collectTempFiles(op.payload)) await offline.fileRemove(t).catch(() => {});
  await offline.queueRemove(opId);
  offline.notifyOfflineChange();
}

// Descarga en el dispositivo todo lo necesario para revisar una planta sin cobertura.
export async function preparePackage({ installId, session, API }) {
  const urls = [`/installations/${installId}`, `/elements?installation_id=${installId}`, "/element-types", `/revision-sessions/active?installation_id=${installId}`, "/spare-parts", "/auth/me"];
  if (session?.id) urls.push(`/incidents?session_id=${session.id}`);
  const res = await Promise.all(urls.map((u) => api.get(u).catch(() => null)));
  const inst = res[0]?.data;
  const elements = res[1]?.data || [];
  const planos = inst?.planos?.length ? inst.planos : inst?.plano_file_id ? [{ id: "principal", file_id: inst.plano_file_id }] : [];
  let planosOk = 0;
  for (const p of planos) {
    if (!p.file_id) continue;
    try {
      const r = await fetch(`${API}/files/${p.file_id}`, { credentials: "include" });
      if (r.ok) { await offline.fileAdd(p.file_id, await r.blob(), `plano-${p.id}.jpg`); planosOk += 1; }
    } catch { /* sin plano: la lista sigue funcionando */ }
  }
  const info = { at: new Date().toISOString(), elements: elements.length, planos: planosOk, session_id: session?.id || null };
  await offline.packageInfoSet(installId, info);
  offline.notifyOfflineChange();
  return info;
}
